import React, {
  useRef,
  useEffect,
} from 'react';
import defer from 'lodash/defer';
import Input from 'pages/Dashboard/components/Input';
import Button from 'pages/Dashboard/components/Button';
import {
  useForm,
} from 'react-hook-form';
import {
  Props,
  TemplateForm,
} from 'views/EMR/components/VisitNoteTemplateDialog';
import Controller from 'core/components/FormController';
import {
  useMixpanelTrack,
  useNotify,
} from 'core/hooks';
import {
  serializeContent,
} from 'views/EMR/Settings/helper';
import {
  useTemplateHooks,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/hooks';
import {
  MasterTemplateDTO,
} from 'dtos';
import {
  notEmptyString,
} from '@populateui/utils';
import {
  settingsEventChannel,
} from 'pages/Dashboard/services/eventChannels';

export default function NewTemplateForm({
  onClose,
  section,
  content,
}: Props) {
  const mixPanelTrack = useMixpanelTrack();
  const notify = useNotify();
  const inputRef = useRef<HTMLInputElement>(null!);

  const hasSection = notEmptyString(section);
  const { useSave } = useTemplateHooks(section);
  const { mutateAsync: saveTemplate } = useSave();

  const {
    handleSubmit,
    reset,
    control,
    formState: { isSubmitting, isValid },
  } = useForm<TemplateForm>({
    mode: 'onChange',
    defaultValues: { templateName: '' },
  });

  const onSubmit = async ({ templateName }: TemplateForm) => {
    try {
      const payload = hasSection ? {
        sectionName: section,
        content: serializeContent(content as string),
      } : (content ?? {}) as MasterTemplateDTO;
      await saveTemplate({
        ...payload,
        templateName,
        isSavingANewTemplate: true,
      });
      notify({
        message: 'New Template Has Been Saved!',
        variant: 'success',
      });
      mixPanelTrack('CreatedVisitNoteTemplate');
      onClose();
      settingsEventChannel.emit('onVisitNoteSettingsChange');
    } finally {
      reset({ templateName }, { keepIsSubmitted: false });
    }
  };

  useEffect(() => {
    defer(() => { inputRef?.current?.focus?.(); });
  }, []);

  return (
    <div className="flex flex-col gap-form-row populate-form h-full">
      <div className="flex-1">
        <Controller
          control={control}
          name="templateName"
          rules={{ required: true }}
          render={({ field, fieldState: { invalid } }) => (
            <Input
              {...field}
              id="template-name"
              label="Template Name"
              required
              inputRef={inputRef}
              placeholder="Name of Template"
              error={invalid}
            />
          )}
        />
      </div>
      <div className="flex gap-4 justify-between">
        <Button
          className="nw-template-cancel-button"
          variant="outlined"
          type="button"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          className="nw-template-save-button"
          color="primary"
          variant="contained"
          type="button"
          disabled={!isValid || isSubmitting}
          onClick={handleSubmit(onSubmit)}
        >
          Save
        </Button>
      </div>
    </div>
  );
}
